import React from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from '@mui/material';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import FundTransfer from './FundTransfer';
import { useAuth } from '../auth/auth-context';
import { applyVanerixBranding } from '../utils/branding';
import { transferFunds } from '../services/fundTransfer';
import { isRecord } from '../utils/backendData';

type ExchangeClient = {
  connectedChannel: number | null;
  clientId: string;
  name: string;
  provider: string;
};

type LocationState = {
  exchangeClients?: ExchangeClient[];
  accountName?: string;
  profileName?: string;
};

function isExchangeClient(value: unknown): value is ExchangeClient {
  return isRecord(value);
}

export default function SatoshiProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { profileId } = useParams();
  const { token } = useAuth();
  const state = (location.state || {}) as LocationState;
  const exchangeClients = Array.isArray(state.exchangeClients)
    ? state.exchangeClients.filter(isExchangeClient)
    : [];
  const accountLabelParts = [state.accountName, state.profileName].filter(Boolean);
  const profileLabel = applyVanerixBranding(
    accountLabelParts.length > 0 ? accountLabelParts.join(' | ') : 'Vanerix Profile'
  );
  const handleOpenTrades = (clientId: string) => {
    if (!profileId) return;
    const client = exchangeClients.find((entry) => entry.clientId === clientId);
    navigate(`/exchange-client-trades/${profileId}/${clientId}`, {
      state: {
        accountName: client?.name,
        provider: client?.provider,
      },
    });
  };

  return (
    <Box
      component="main"
      sx={{
        minHeight: 'calc(100vh - 64px)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'flex-start',
        bgcolor: 'background.default',
        p: 2,
        pt: 4,
      }}
    >
      <Paper elevation={3} sx={{ p: 4, maxWidth: 1100, width: '100%', borderRadius: '15px' }}>
        <Stack spacing={2}>
          <Box>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: '999px',
                textTransform: 'none',
                fontSize: 12,
                fontWeight: 600,
                px: 4,
                py: 0.25,
                minWidth: 140,
                color: 'grey.800',
                borderColor: 'grey.300',
                bgcolor: 'grey.100',
                boxShadow: '0 6px 14px rgba(17, 24, 39, 0.08)',
                '&:hover': {
                  bgcolor: 'grey.200',
                  borderColor: 'grey.400',
                },
              }}
            >
              BACK
            </Button>
          </Box>

          <Stack direction="row" justifyContent="center">
            <Tooltip title="Exchange clients linked to this profile" arrow>
              <Chip
                label={profileLabel}
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.6,
                  fontSize: 16,
                  bgcolor: 'grey.200',
                  color: 'grey.700',
                  borderRadius: '999px',
                  px: 1.5,
                  py: 2,
                  boxShadow: '0 8px 18px rgba(17, 24, 39, 0.15)',
                }}
              />
            </Tooltip>
          </Stack>

          {exchangeClients.length > 0 && (
            <FundTransfer
              title="BING-X FUNDS TRANSFER"
              accounts={exchangeClients.map((c) => ({ id: c.clientId, name: c.name }))}
              token={token}
              onTransfer={(tk, fromId, toId, amount) =>
                transferFunds(tk, {
                  profileId: profileId!,
                  fromExchangeClientId: fromId,
                  toExchangeClientId: toId,
                  amount,
                })
              }
            />
          )}

          {exchangeClients.length > 0 ? (
            <TableContainer
              component={Paper}
              variant="outlined"
              sx={{
                width: '100%',
                borderRadius: '15px',
                overflow: 'hidden',
                borderColor: 'grey.200',
                boxShadow: '0 8px 20px rgba(17, 24, 39, 0.08)',
              }}
            >
              <Table size="small" aria-label="exchange clients">
                <TableHead>
                  <TableRow
                    sx={{
                      bgcolor: 'grey.200',
                      background: 'linear-gradient(90deg, #e7eaee 0%, #dfe4ea 100%)',
                    }}
                  >
                    <TableCell
                      align="left"
                      sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700', textTransform: 'uppercase' }}
                    >
                      Account Name
                    </TableCell>
                    <TableCell
                      align="left"
                      sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700', textTransform: 'uppercase' }}
                    >
                      Exchange
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontSize: 15, fontWeight: 600, color: 'grey.700', textTransform: 'uppercase' }}
                    >
                      Connected Channel
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {exchangeClients.map((client, index) => (
                    <TableRow
                      key={`${client.name}-${client.provider}-${index}`}
                      hover
                      role="button"
                      tabIndex={0}
                      onClick={() => handleOpenTrades(client.clientId)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleOpenTrades(client.clientId);
                        }
                      }}
                      sx={{
                        cursor: 'pointer',
                        transition: 'background-color 150ms ease, transform 150ms ease',
                        '&:hover': { bgcolor: 'grey.50' },
                      }}
                    >
                      <TableCell align="left" sx={{ fontSize: 16, color: 'grey.900' }}>
                        {client.name}
                      </TableCell>
                      <TableCell align="left" sx={{ fontSize: 16, color: 'grey.900' }}>
                        {client.provider}
                      </TableCell>
                       <TableCell align="center" sx={{ fontSize: 16, color: 'grey.900' }}>
                        {client.connectedChannel ?? '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">No exchange clients found.</Alert>
          )}
        </Stack>
      </Paper>
    </Box>
  );
}
